import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Send, MessageCircle, Clock, CheckCircle } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { API } from '../App';

const ConversationPage = () => {
  const { conversationId } = useParams();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const fetchConversation = async () => {
    try {
      const response = await fetch(`${API}/conversations/${conversationId}`);
      if (response.ok) {
        const data = await response.json();
        setConversation(data.conversation);
        setMessages(data.messages);
      }
    } catch (error) {
      console.error('Error fetching conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    
    setSending(true);
    try {
      const response = await fetch(`${API}/conversations/${conversationId}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reply_text: replyText })
      });
      
      if (response.ok) {
        setReplyText('');
        fetchConversation();
        toast.success('Message envoyé !');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    fetchConversation();
    // Refresh every 30 seconds
    const interval = setInterval(fetchConversation, 30000);
    return () => clearInterval(interval);
  }, [conversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', { 
      day: '2-digit', 
      month: 'long',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="spinner w-12 h-12"></div>
        </div>
      </Layout>
    );
  }

  if (!conversation) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center py-20">
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-4">Conversation non trouvée</h1>
          <p className="text-slate-600 mb-8">Cette conversation n'existe pas ou a été supprimée.</p>
          <Link to="/contact">
            <Button className="btn-primary">Nous contacter</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#F9F9F7] py-8" data-testid="conversation-page">
        <div className="max-w-3xl mx-auto px-6">
          {/* Header */}
          <div className="mb-6">
            <Link to="/contact" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#006F33] mb-4">
              <ArrowLeft className="w-4 h-4" />
              Retour au contact
            </Link>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-xl font-bold text-[#1A1A1A]">{conversation.subject}</h1>
                  <p className="text-slate-500 mt-1">
                    Conversation démarrée le {formatTime(conversation.created_at)}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  conversation.status === 'open' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-slate-100 text-slate-500'
                }`}>
                  {conversation.status === 'open' ? 'En cours' : 'Fermée'}
                </span>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 bg-[#006F33]/5">
              <div className="flex items-center gap-2 text-sm text-[#006F33]">
                <MessageCircle className="w-4 h-4" />
                <span>Échanges avec Teranga Global Legal</span>
              </div>
            </div>

            <div className="p-6 space-y-4 min-h-[400px] max-h-[500px] overflow-y-auto">
              {messages.map((msg) => (
                <div
                  key={msg.message_id}
                  className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] rounded-2xl px-5 py-3 ${
                    msg.sender_type === 'client'
                      ? 'bg-[#006F33] text-white rounded-br-sm'
                      : 'bg-slate-100 text-[#1A1A1A] rounded-bl-sm'
                  }`}>
                    {msg.sender_type === 'admin' && (
                      <p className={`text-xs font-medium mb-1 text-[#006F33]`}>
                        {msg.sender_name} - Teranga Global Legal
                      </p>
                    )}
                    <p className="whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-xs mt-2 ${msg.sender_type === 'client' ? 'text-white/70' : 'text-slate-400'}`}>
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Section */}
            {conversation.status === 'open' ? (
              <div className="p-4 border-t border-slate-100 bg-slate-50">
                <div className="flex gap-3">
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Écrivez votre message..."
                    className="flex-1 resize-none bg-white"
                    rows={3}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendReply();
                      }
                    }}
                    data-testid="client-reply-input"
                  />
                  <Button
                    onClick={sendReply}
                    disabled={sending || !replyText.trim()}
                    className="btn-primary self-end"
                    data-testid="client-send-btn"
                  >
                    {sending ? (
                      <div className="spinner w-5 h-5 border-white"></div>
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Appuyez sur Entrée pour envoyer • Notre équipe vous répondra dans les plus brefs délais
                </p>
              </div>
            ) : (
              <div className="p-4 border-t border-slate-100 bg-slate-50 text-center">
                <p className="text-slate-500">
                  Cette conversation est fermée. Pour une nouvelle demande, veuillez{' '}
                  <Link to="/contact" className="text-[#006F33] hover:underline">nous contacter</Link>.
                </p>
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="mt-6 bg-[#E6C200]/10 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-[#B39500] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-[#1A1A1A]">Temps de réponse</p>
                <p className="text-sm text-slate-600">
                  Notre équipe répond généralement dans un délai de 24 heures ouvrées.
                  Pour les urgences, contactez-nous par téléphone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ConversationPage;
